<script>
  // Этап 33: шторка «Модели провайдеров» у владельца сервера — что отдают
  // `GET /models` провайдеров: новые модели сверху, спрятанные правилами — под
  // «Показать скрытые», пропавшие и незагруженные (Home) — с пометкой. Открыл
  // шторку — метки «новая» снимаются (чип на строке моделей гаснет).
  // Этап 33.2: переключатель «в выборе» (включение — с пробным запросом к модели),
  // выключение модели с чатами — с подтверждением, «Сделать основной».
  import { createEventDispatcher } from 'svelte';
  import { api } from '../api.js';
  import { brainStatusStore, loadModels } from '../stores.js';
  import { TEXT } from '../ui-text.js';
  import { catalogErrorText, catalogGroups, disableConsequence, formatMoment, modelLabel } from '../model-picker.js';
  import BottomSheet from './BottomSheet.svelte';
  import ProviderModelRow from './ProviderModelRow.svelte';

  export let open = false;

  const dispatch = createEventDispatcher();

  let loading = false;
  let loadError = '';
  let catalog = null;
  let expanded = {};
  let wasOpen = false;
  let busy = {};
  let errors = {};
  let confirming = null;
  let actionsFor = null;

  $: if (open && !wasOpen) {
    wasOpen = true;
    load();
  } else if (!open && wasOpen) {
    wasOpen = false;
  }
  $: groups = catalogGroups(catalog);
  $: defaultModel = catalog?.defaultModel || null;

  async function load() {
    loading = true;
    loadError = '';
    expanded = {};
    busy = {};
    errors = {};
    confirming = null;
    actionsFor = null;
    try {
      catalog = await api.getModelCatalog();
    } catch (error) {
      catalog = null;
      loadError = TEXT.modelCatalog.loadFailed;
      return;
    } finally {
      loading = false;
    }
    if (catalog?.newCount > 0) {
      markSeen();
    }
  }

  async function markSeen() {
    try {
      await api.markModelCatalogSeen();
      brainStatusStore.update((value) => ({ ...value, newModels: 0 }));
    } catch (error) {
      // Метки «новая» останутся до следующего открытия — список всё равно показан.
      console.warn('[models] mark seen failed', error);
    }
  }

  async function change(item, action, body) {
    busy = { ...busy, [item.id]: action };
    errors = { ...errors, [item.id]: '' };
    try {
      catalog = await api.updateModelCatalog({ model: item.id, ...body });
      confirming = null;
      actionsFor = null;
      // Выбор модели на этом устройстве — сразу; остальным придёт models_changed.
      loadModels().catch((error) => console.warn('[models] reload failed', error));
    } catch (error) {
      errors = { ...errors, [item.id]: catalogErrorText(error) };
    } finally {
      const { [item.id]: _done, ...rest } = busy;
      busy = rest;
    }
  }

  function handleEnable(event) {
    change(event.detail, 'enable', { enabled: true });
  }

  function handleDisable(event) {
    const item = event.detail;
    // Без чатов, шаблонов и умолчаний выключать нечего подтверждать.
    if (disableConsequence(item, defaultModel)) {
      confirming = item.id;
      return;
    }
    change(item, 'disable', { enabled: false });
  }

  function handleConfirm(event) {
    change(event.detail, 'disable', { enabled: false });
  }

  function handleDefault(event) {
    change(event.detail, 'default', { isDefault: true });
  }

  function toggleActions(event) {
    actionsFor = actionsFor === event.detail.id ? null : event.detail.id;
  }

  function roleLabels(item) {
    return [...new Set((item.roles || []).map((role) => TEXT.modelCatalog.roles[role] || role))].join(', ');
  }

  function warningText(warning) {
    const reason = TEXT.brain.warningReasons[warning.reason] || warning.reason;
    return warning.reason === 'default_replaced' && warning.replacedBy
      ? `${reason} ${modelLabel(warning.replacedBy)}`
      : reason;
  }

  function toggleHidden(provider) {
    expanded = { ...expanded, [provider]: !expanded[provider] };
  }

  function close() {
    dispatch('close');
  }
</script>

<BottomSheet {open} title={TEXT.modelCatalog.title} on:close={close}>
  <div class="catalog">
    <p class="intro">{TEXT.modelCatalog.intro}</p>
    {#if loading}
      <p class="muted">{TEXT.modelCatalog.loading}</p>
    {:else if loadError}
      <p class="muted" role="alert">{loadError}</p>
      <button type="button" class="secondary" on:click={load}>{TEXT.modelCatalog.retry}</button>
    {:else if catalog}
      {#if catalog.warnings?.length}
        <section class="warnings" aria-label={TEXT.brain.warningsTitle}>
          <h3>{TEXT.brain.warningsTitle}</h3>
          <ul>
            {#each catalog.warnings as warning (warning.model)}
              <li>
                <span class="name">{modelLabel(warning.model)}</span>
                <span class="meta">
                  {warningText(warning)}{#if warning.roles?.length} · {roleLabels(warning)}{/if}
                </span>
              </li>
            {/each}
          </ul>
        </section>
      {/if}
      {#each groups as group (group.provider)}
        <section class="group" aria-label={group.title}>
          <header>
            <span class="dot" data-state={group.state} aria-hidden="true"></span>
            <h3>{group.title}</h3>
            <span class="listed">
              {group.listedAt ? `${TEXT.modelCatalog.listedAt} ${formatMoment(group.listedAt)}` : TEXT.modelCatalog.noList}
            </span>
          </header>
          {#if group.shown.length}
            <ul class="models">
              {#each group.shown as item (item.id)}
                <ProviderModelRow
                  {item}
                  {defaultModel}
                  busy={busy[item.id] || null}
                  error={errors[item.id] || ''}
                  confirming={confirming === item.id}
                  actionsOpen={actionsFor === item.id}
                  on:enable={handleEnable}
                  on:disable={handleDisable}
                  on:confirm={handleConfirm}
                  on:cancel={() => (confirming = null)}
                  on:default={handleDefault}
                  on:actions={toggleActions}
                />
              {/each}
            </ul>
          {:else}
            <p class="muted">{TEXT.modelCatalog.empty}</p>
          {/if}
          {#if group.hidden.length}
            <button
              type="button"
              class="link"
              aria-expanded={Boolean(expanded[group.provider])}
              on:click={() => toggleHidden(group.provider)}
            >
              {expanded[group.provider] ? TEXT.modelCatalog.hideHidden : `${TEXT.modelCatalog.showHidden} (${group.hidden.length})`}
            </button>
            {#if expanded[group.provider]}
              <p class="muted small">{TEXT.modelCatalog.hiddenHint}</p>
              <ul class="models">
                {#each group.hidden as item (item.id)}
                  <ProviderModelRow
                    {item}
                    {defaultModel}
                    busy={busy[item.id] || null}
                    error={errors[item.id] || ''}
                    on:enable={handleEnable}
                  />
                {/each}
              </ul>
            {/if}
          {/if}
        </section>
      {/each}
    {/if}
    <button type="button" class="close" on:click={close}>{TEXT.modelCatalog.close}</button>
  </div>
</BottomSheet>

<style>
  .catalog {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .intro,
  .muted {
    margin: 0;
    line-height: 1.4;
  }

  .muted {
    color: var(--text-secondary);
  }

  .small {
    font-size: 0.8125rem;
  }

  h3 {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 600;
  }

  .group,
  .warnings {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .listed {
    margin-left: auto;
    font-size: 0.8125rem;
    color: var(--text-secondary);
  }

  .dot {
    width: 0.5rem;
    height: 0.5rem;
    flex: none;
    border-radius: 50%;
    background: var(--text-tertiary, var(--text-secondary));
    opacity: 0.6;
  }

  .dot[data-state='ok'] {
    background: var(--success-color, #2e9d5b);
    opacity: 1;
  }

  .dot[data-state='down'] {
    background: var(--error-text);
    opacity: 1;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .warnings li {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 0.5rem;
    padding: 0.5rem 0.75rem;
    border-radius: 10px;
    background: var(--warning-bg);
    color: var(--warning-text);
  }

  .name {
    font-weight: 600;
  }

  .meta {
    font-size: 0.8125rem;
  }

  .link {
    align-self: flex-start;
    min-height: 44px;
    padding: 0 0.25rem;
    border: none;
    background: none;
    color: var(--accent-color, var(--text-primary));
    font: inherit;
    cursor: pointer;
  }

  .secondary,
  .close {
    align-self: stretch;
    min-height: 44px;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    background: var(--bg-tertiary);
    color: var(--text-primary);
    font: inherit;
    font-weight: 500;
    cursor: pointer;
  }
</style>
